import type { DashboardOverview, StaffNotification, StaffNotificationList } from '@erp/shared';
import {
  Activity,
  AlertCircle,
  Banknote,
  Bell,
  BellRing,
  Boxes,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  Globe,
  HandCoins,
  type LucideIcon,
  Moon,
  Package,
  PackagePlus,
  PackageX,
  Receipt,
  Scale,
  ScanLine,
  ShoppingCart,
  Smartphone,
  Sun,
  Sunrise,
  TrendingUp,
  UserPlus,
  Wallet,
} from 'lucide-react';
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { AnimatedNumber, Badge, Button, Card, Skeleton, StatCard } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatCompact, formatDate, formatDateTime } from '../../lib/intl.js';
import { moduleEnabled } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { formatMoney } from '../../lib/money.js';
import { AttendanceClockCard } from '../hr/attendance/AttendanceClockCard.js';
import { bodyOf, EVENT_ICONS, titleOf } from '../notifications/NotificationsBell.js';
import { CHART_COLORS, SERIES_PALETTE } from '../reports/_shared/chartColors.js';

import { ExchangeRateCard } from './ExchangeRateCard.js';
import { QuickActions } from './QuickActions.js';

interface InvoiceRow {
  id: string;
  docNumber: string;
  docDate: string;
  status: string;
  total: string;
  currencyCode: string;
}

const statusTone: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  draft: 'neutral', issued: 'info', paid: 'success', cancelled: 'danger',
  submitted: 'info', fulfilled: 'success', accepted: 'success', rejected: 'danger',
};
const opTone: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  CREATE: 'success', UPDATE: 'info', DELETE: 'danger',
};

export function DashboardPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { t: tDoc } = useTranslation('documents');
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);

  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  // The owner's branch switcher — narrows every tile to one branch. Only shown
  // when the caller can see more than one branch (the endpoint is scope-narrowed).
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [branchFilter, setBranchFilter] = useState('all');
  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  const [notifs, setNotifs] = useState<StaffNotification[] | null>(null);
  const [installmentsOverdue, setInstallmentsOverdue] = useState<number | null>(null);
  const [installmentsUpcoming, setInstallmentsUpcoming] = useState<number | null>(null);

  const enabledModules = useEnabledModules(tenant?.id);
  // Installment tiles only matter when the module is on AND there's activity — a
  // store that doesn't do installments shouldn't see "0 overdue / 0 upcoming".
  const installmentsModuleOn = moduleEnabled(enabledModules, 'installments');
  const canSeeInstallments = usePermission('installments.read') && installmentsModuleOn;
  const canReadInvoices = usePermission('invoices.read');
  const canReadItems = usePermission('items.read');
  const canReadStock = usePermission('stock.read');
  // The empty state's "New invoice" button — the same gate the list page and the
  // quick actions use, so manual entry is either offered everywhere or nowhere.
  const canCreateInvoice = usePermission('invoices.write') && moduleEnabled(enabledModules, 'manual_sales');
  // Staff linked to an employee clock in from here — the page they land on. The
  // card renders nothing for anyone without an employee record.
  const canClock = usePermission('hr.self.time.entry') && moduleEnabled(enabledModules, 'hr');

  // Recharts tooltip chrome bound to theme tokens (dark-mode + brand aware).
  const tooltipStyle: CSSProperties = {
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: '0.5rem',
    fontSize: '0.75rem',
    color: 'var(--color-fg)',
  };

  const baseCurrency = overview?.baseCurrencyCode ?? tenant?.baseCurrencyCode ?? 'USD';

  useEffect(() => {
    void api().get<{ items: Array<{ id: string; name: string }> }>('/warehouses')
      .then((r) => setWarehouses(r.data.items))
      .catch(() => setWarehouses([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingOverview(true);
    const branchParams = branchFilter !== 'all' ? { warehouseId: branchFilter } : {};
    void api()
      .get<DashboardOverview>('/reports/dashboard', { params: branchParams })
      .then((r) => { if (!cancelled) setOverview(r.data); })
      .catch(() => { if (!cancelled) setOverview(null); })
      .finally(() => { if (!cancelled) setLoadingOverview(false); });
    if (canReadInvoices) {
      void api()
        .get<{ items: InvoiceRow[] }>('/invoices', { params: branchParams })
        .then((r) => { if (!cancelled) setInvoices(r.data.items.slice(0, 5)); })
        .catch(() => { if (!cancelled) setInvoices([]); });
    }
    // The bell's feed, on the page itself — same endpoint, same wording. Read
    // only: no mark-all-seen here, or the bell badge would silently clear.
    void api()
      .get<StaffNotificationList>('/notifications', { params: { limit: 8 } })
      .then((r) => { if (!cancelled) setNotifs(r.data.items); })
      .catch(() => { if (!cancelled) setNotifs([]); });
    if (canSeeInstallments) {
      // On error, -1 = "failed/hidden" (null stays reserved for "still loading",
      // so the tile can't spin forever after a transient failure).
      void api()
        .get<{ items: unknown[] }>('/installments/reports/overdue')
        .then((r) => { if (!cancelled) setInstallmentsOverdue(r.data.items.length); })
        .catch(() => { if (!cancelled) setInstallmentsOverdue(-1); });
      void api()
        .get<{ items: unknown[] }>('/installments/reports/upcoming?windowDays=30')
        .then((r) => { if (!cancelled) setInstallmentsUpcoming(r.data.items.length); })
        .catch(() => { if (!cancelled) setInstallmentsUpcoming(-1); });
    }
    return () => { cancelled = true; };
  }, [canSeeInstallments, canReadInvoices, branchFilter]);

  const fmt = (amt: string | null | undefined, ccy = baseCurrency): string =>
    formatMoney(amt ?? '0', { locale: i18n.language, currency: ccy });
  const moneyFmt = (n: number): string => fmt(String(n));
  const fmtCount = (n: number): string => String(Math.round(n));
  // Compact axis labels for the chart (e.g. 12.5k) — money without a currency symbol.
  const fmtShort = (n: number): string => {
    try { return formatCompact(n, { locale: i18n.language }); }
    catch { return String(Math.round(n)); }
  };
  const fmtDateTime = (iso: string): string => {
    try { return formatDateTime(iso, { locale: i18n.language }, { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  };
  // Online orders are always shown in Iraq time (Asia/Baghdad, UTC+3, no DST).
  const iraqTime = (iso: string): string => {
    try { return formatDateTime(iso, { locale: i18n.language, timeZone: 'Asia/Baghdad' }); }
    catch { return iso; }
  };
  const weekday = (d: string): string => {
    // Label with the UTC weekday so it matches the backend's UTC day buckets.
    try { return formatDate(`${d}T00:00:00Z`, { locale: i18n.language, timeZone: 'UTC' }, { weekday: 'short' }); }
    catch { return d.slice(5); }
  };

  const ov = overview;
  const loading = loadingOverview;
  // A "today" tile is shown while loading (perms unknown) and afterwards only if the
  // caller could see that section (the endpoint returns null for the rest).
  const today = ov?.today;
  const todayStr = new Date().toISOString().slice(0, 10);
  // Combined "revenue today" = POS + non-POS invoices (disjoint sets, no double count).
  const revenueToday = today && (today.pos || today.invoices)
    ? Number(today.pos?.total ?? 0) + Number(today.invoices?.total ?? 0)
    : null;
  const trend = ov?.salesTrend ?? [];
  const trendHasData = trend.some((d) => Number(d.total) > 0);
  const sellers = ov?.topSellers ?? [];
  const lowStock = ov?.lowStock ?? [];
  // "Today by channel" donut — each channel's monetary total today (zero slices dropped).
  const channelData = today
    ? [
        { key: 'pos', name: t('dashboard.channel.pos', { defaultValue: 'POS' }), value: Number(today.pos?.total ?? 0) },
        { key: 'invoices', name: t('dashboard.channel.invoices', { defaultValue: 'Invoices' }), value: Number(today.invoices?.total ?? 0) },
        { key: 'orders', name: t('dashboard.channel.orders', { defaultValue: 'Orders' }), value: Number(today.orders?.total ?? 0) },
      ].filter((d) => d.value > 0)
    : [];
  // Whether each chart will render — used to drop the whole charts row (and its
  // grid gap) for roles that can see neither.
  const showTrend = loading || trend.length > 0;
  const showChannel = loading || channelData.length > 0;

  // Today-vs-yesterday momentum on the Revenue tile (only when yesterday had sales
  // and the change is non-zero — a "▲ 0%" pill is noise).
  const revenueYesterday = ov?.revenueYesterday != null ? Number(ov.revenueYesterday) : null;
  const revenuePct = (revenueToday !== null && revenueYesterday !== null && revenueYesterday > 0)
    ? Math.round(((revenueToday - revenueYesterday) / revenueYesterday) * 100)
    : null;
  const revenueDelta = revenuePct !== null && revenuePct !== 0
    ? { value: `${Math.abs(revenuePct)}%`, positive: revenuePct > 0 }
    : null;

  // Time-of-day greeting with the signed-in user's first name.
  const hour = new Date().getHours();
  const greetKey = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const firstName = (user?.fullName ?? '').trim().split(/\s+/)[0] ?? '';
  const greeting = t(`dashboard.greeting.${greetKey}`, { defaultValue: 'Welcome back' });
  const GreetIcon = greetKey === 'morning' ? Sunrise : greetKey === 'afternoon' ? Sun : Moon;
  const dateLabel = (() => {
    try { return formatDate(new Date(), { locale: i18n.language }, { weekday: 'long', day: 'numeric', month: 'long' }); }
    catch { return todayStr; }
  })();

  return (
    <div className="flex flex-col gap-6">
      {/* Greeting hero — brand-tinted, with the time-of-day icon, tenant chip and date */}
      <Card className="relative overflow-hidden bg-gradient-to-br from-brand/12 via-bg-subtle to-bg-subtle p-6">
        <span className="pointer-events-none absolute -end-12 -top-12 h-44 w-44 rounded-full bg-brand/15 blur-3xl" aria-hidden />
        <div className="relative flex flex-wrap items-center gap-4">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-brand/15 text-brand shadow-sm">
            <GreetIcon className="h-7 w-7" aria-hidden />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold">{greeting}{firstName ? `, ${firstName}` : ''}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-fg-muted">
              {tenant && <span className="font-medium text-fg">{tenant.name}</span>}
              <span className="rounded-full bg-brand/10 px-2 py-0.5 text-xs font-semibold text-brand">{baseCurrency}</span>
              <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" aria-hidden />{dateLabel}</span>
            </div>
          </div>
          {warehouses.length > 1 && (
            <label className="ms-auto flex flex-col gap-1">
              <span className="text-xs font-medium text-fg-muted">{t('filters.branch', { defaultValue: 'Branch' })}</span>
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand"
              >
                <option value="all">{t('filters.allBranches', { defaultValue: 'All branches' })}</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      </Card>

      {canClock && <AttendanceClockCard compact />}

      {/* Today at a glance — operational counts (not balances) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {(loading || revenueToday !== null) && (
          <Kpi label={t('dashboard.revenueToday', { defaultValue: 'Revenue today' })}
               amount={revenueToday} format={moneyFmt} delta={revenueDelta}
               loading={loading} icon={Banknote} spine="var(--viz-3)" />
        )}
        {(loading || today?.invoices) && (
          <Kpi label={t('dashboard.invoicesToday', { defaultValue: 'Invoices today' })}
               amount={today?.invoices ? today.invoices.count : null} format={fmtCount}
               hint={today?.invoices ? fmt(today.invoices.total) : undefined}
               loading={loading} linkTo="/sales/invoices" icon={Receipt} spine="var(--viz-1)" />
        )}
        {(loading || today?.orders) && (
          <Kpi label={t('dashboard.ordersToday', { defaultValue: 'Orders today' })}
               amount={today?.orders ? today.orders.count : null} format={fmtCount}
               hint={today?.orders ? fmt(today.orders.total) : undefined}
               loading={loading} linkTo="/sales/orders" icon={ShoppingCart} spine="var(--viz-4)" />
        )}
        {(loading || today?.pos) && (
          <Kpi label={t('dashboard.posToday', { defaultValue: 'POS sales today' })}
               amount={today?.pos ? today.pos.count : null} format={fmtCount}
               hint={today?.pos ? fmt(today.pos.total) : undefined}
               loading={loading} linkTo="/pos/sales" icon={ScanLine} spine="var(--viz-2)" />
        )}
        {(loading || today?.payments) && (
          <Kpi label={t('dashboard.paymentsToday', { defaultValue: 'Payments received today' })}
               amount={today?.payments ? today.payments.count : null} format={fmtCount}
               hint={today?.payments ? fmt(today.payments.total) : undefined}
               loading={loading} linkTo="/payments" icon={HandCoins} spine="var(--viz-5)" />
        )}
        {(loading || today?.expenses) && (
          <Kpi label={t('dashboard.expensesToday', { defaultValue: 'Expenses today' })}
               amount={today?.expenses ? today.expenses.count : null} format={fmtCount}
               hint={today?.expenses ? fmt(today.expenses.total) : undefined}
               loading={loading} linkTo="/expenses" icon={Wallet} spine="var(--viz-6)" />
        )}
        {/* Money in minus money out. Only rendered when the server could see
            both halves — it sends null rather than a half-truth. */}
        {ov?.netToday != null && (
          <Kpi label={t('dashboard.netToday', { defaultValue: 'Net today' })}
               amount={Number(ov.netToday)} format={moneyFmt}
               loading={loading} icon={Scale} spine="var(--viz-3)" />
        )}
        {(loading || today?.unitsSold != null) && (
          <Kpi label={t('dashboard.itemsSoldToday', { defaultValue: 'Items sold today' })}
               amount={today?.unitsSold ?? null} format={fmtCount}
               loading={loading} linkTo="/pos/sales" icon={Boxes} spine="var(--viz-6)" />
        )}
        {(loading || today?.newCustomers != null) && (
          <Kpi label={t('dashboard.newCustomersToday', { defaultValue: 'New customers today' })}
               amount={today?.newCustomers ?? null} format={fmtCount}
               loading={loading} linkTo="/sales/parties" icon={UserPlus} spine="var(--viz-4)" />
        )}
        {/* Only surfaced when there's something to act on — no "0 overdue" noise. */}
        {canSeeInstallments && installmentsOverdue !== null && installmentsOverdue > 0 && (
          <Kpi label={t('dashboard.installmentsOverdue', { defaultValue: 'Overdue installments' })}
               amount={installmentsOverdue} format={fmtCount}
               linkTo="/installments/reports" icon={AlertCircle} alert />
        )}
        {canSeeInstallments && installmentsUpcoming !== null && installmentsUpcoming > 0 && (
          <Kpi label={t('dashboard.installmentsUpcoming', { defaultValue: 'Upcoming (30d)' })}
               amount={installmentsUpcoming} format={fmtCount}
               linkTo="/installments/reports" icon={CalendarClock} spine="var(--viz-6)" />
        )}
      </div>
      {/* How much of the shop there is. A plain strip, not tiles: these are
          reference figures you glance at, not today's numbers you act on. */}
      {ov?.catalogue && (
        <Card>
          <h2 className="mb-3 text-sm font-semibold text-fg-muted">
            {t('dashboard.catalogue.title', { defaultValue: 'Your shop' })}
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {([
              ['products', ov.catalogue.products, '/inventory/items'],
              ['brands', ov.catalogue.brands, '/inventory/brands'],
              ['categories', ov.catalogue.categories, '/inventory/categories'],
              ['customers', ov.catalogue.customers, '/sales/parties'],
              ['suppliers', ov.catalogue.suppliers, '/purchasing/parties'],
              ['warehouses', ov.catalogue.warehouses, '/inventory/warehouses'],
            ] as const)
              // A null means 'you may not see this', which is not the same as a
              // zero — so the entry is dropped rather than shown as none.
              .filter(([, n]) => n !== null && n !== undefined)
              .map(([key, n, to]) => (
                <Link key={key} to={to} className="rounded-lg border border-border p-3 transition hover:border-brand">
                  <div className="text-xl font-semibold tabular-nums text-fg">{formatCompact(Number(n), { locale: i18n.language })}</div>
                  <div className="text-xs text-fg-muted">{t(`dashboard.catalogue.${key}`, { defaultValue: key })}</div>
                </Link>
              ))}
          </div>
        </Card>
      )}

      {/* What actually moved today. Distinct from the activity feed below,
          which reports who EDITED what — a different question. */}
      {ov?.todayTransactions && ov.todayTransactions.length > 0 && (
        <Card>
          <h2 className="mb-3 text-sm font-semibold text-fg-muted">
            {t('dashboard.todayTransactions.title', { defaultValue: "Today's transactions" })}
          </h2>
          <ul className="divide-y divide-border">
            {ov.todayTransactions.map((x) => (
              <li key={`${x.kind}:${x.id}`} className="flex items-center gap-3 py-2 text-sm">
                <Badge tone="neutral">{t(`dashboard.todayTransactions.kind.${x.kind}`, { defaultValue: x.kind })}</Badge>
                <span className="font-mono text-xs text-fg-muted">{x.reference}</span>
                <span className="min-w-0 flex-1 truncate">{x.party ?? ''}</span>
                {/* Negative = money out. Coloured, and the sign kept, so a
                    column of figures cannot be misread as all income. */}
                <span className={`tabular-nums ${Number(x.amount) < 0 ? 'text-danger' : 'text-fg'}`}>
                  {formatMoney(x.amount, { locale: i18n.language, currency: x.currencyCode })}
                </span>
                <span className="w-12 shrink-0 text-end text-xs text-fg-muted">
                  {new Date(x.at).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}


      {/* Today's exchange rate, editable in place — the owner's first errand of
          the morning gets a first-class spot instead of a manual journal.
          Renders nothing for single-currency shops or without exchange_rates.read. */}
      <ExchangeRateCard baseCurrency={baseCurrency} />

      {/* New online orders — a live feed of storefront/app orders (source + version +
          Iraq-time timestamp). Only present for users who can see online orders. */}
      {(ov?.recentOnlineOrders?.length ?? 0) > 0 && (
        <Card className="overflow-hidden p-0">
          <header className="flex items-center justify-between gap-2 p-4 pb-2">
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <BellRing className="h-4 w-4 text-brand" aria-hidden />
              {t('dashboard.newOrders', { defaultValue: 'New online orders' })}
              <span className="rounded-full bg-brand/10 px-2 py-0.5 text-xs font-bold text-brand">{ov?.recentOnlineOrders.length}</span>
            </h2>
            <Link to="/cms/orders"><Button variant="ghost" size="sm">{t('actions.viewAll', { defaultValue: 'View all' })}</Button></Link>
          </header>
          <div className="flex flex-col divide-y divide-border">
            {ov?.recentOnlineOrders.map((o) => {
              const isApp = o.source === 'app';
              const SourceIcon = isApp ? Smartphone : Globe;
              return (
                <Link key={o.id} to={`/cms/orders/${o.id}`} className="flex items-center gap-3 px-4 py-3 transition hover:bg-bg-subtle">
                  <span className={`grid h-9 w-9 flex-none place-items-center rounded-lg ${isApp ? 'bg-brand/10 text-brand' : 'bg-bg-muted text-fg-muted'}`}>
                    <SourceIcon className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-semibold text-fg">{o.orderNumber}</span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-bg-subtle px-2 py-0.5 text-[0.65rem] font-medium text-fg-muted">
                        <SourceIcon className="h-3 w-3" aria-hidden />
                        {isApp ? t('dashboard.sourceApp', { defaultValue: 'App' }) : t('dashboard.sourceWeb', { defaultValue: 'Website' })}
                        {o.version ? ` · v${o.version}` : ''}
                      </span>
                      <Badge tone={statusTone[o.status] ?? 'neutral'}>{o.status}</Badge>
                    </div>
                    <span className="mt-0.5 block truncate text-xs text-fg-muted">
                      {o.customerName ?? '—'} · {iraqTime(o.createdAt)}
                    </span>
                  </div>
                  <span className="flex-none font-mono text-sm font-semibold">{fmt(o.total, o.currencyCode)}</span>
                </Link>
              );
            })}
          </div>
        </Card>
      )}

      {/* Charts — 7-day sales trend (today highlighted) + today-by-channel donut */}
      {(showTrend || showChannel) && (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title={t('dashboard.salesTrend', { defaultValue: 'Sales — last 7 days' })}
          loading={loading} hide={!loading && trend.length === 0}
          empty={trend.length > 0 && !trendHasData}
          emptyText={t('dashboard.noSales', { defaultValue: 'No sales recorded yet.' })}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trend} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
              <CartesianGrid stroke={CHART_COLORS.border} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: CHART_COLORS.fgMuted }} tickFormatter={weekday} />
              <YAxis tick={{ fontSize: 11, fill: CHART_COLORS.fgMuted }} tickFormatter={fmtShort} width={52} />
              <Tooltip cursor={{ fill: CHART_COLORS.brandSubtle, opacity: 0.4 }} contentStyle={tooltipStyle}
                formatter={(v) => [fmt(String(v ?? 0)), t('dashboard.revenue', { defaultValue: 'Revenue' })]}
                labelFormatter={(d) => String(d ?? '')} />
              <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                {trend.map((d) => (
                  <Cell key={d.date} fill={d.date === todayStr ? CHART_COLORS.brand : CHART_COLORS.brandSubtle} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t('dashboard.byChannel', { defaultValue: "Today's sales by channel" })}
          loading={loading} hide={!loading && channelData.length === 0}
          emptyText={t('dashboard.noSales', { defaultValue: 'No sales recorded yet.' })}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={channelData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                innerRadius={48} outerRadius={82} paddingAngle={2} stroke="var(--color-bg)">
                {channelData.map((d, i) => <Cell key={d.key} fill={SERIES_PALETTE[i % SERIES_PALETTE.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmt(String(v ?? 0)), String(n ?? '')]} />
              <Legend verticalAlign="bottom" height={28} iconType="circle" wrapperStyle={{ fontSize: '0.72rem' }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      )}

      {/* Latest documents — last order / invoice / product added */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <LatestCard icon={Receipt} title={t('dashboard.lastInvoice', { defaultValue: 'Last invoice' })} loading={loading}
          primary={ov?.lastInvoice?.docNumber} to={ov?.lastInvoice ? `/sales/invoices/${ov.lastInvoice.id}` : undefined}
          secondary={ov?.lastInvoice ? formatDate(ov.lastInvoice.docDate, { locale: i18n.language }) : undefined}
          badge={ov?.lastInvoice ? { text: tDoc(`invoices.status.${ov.lastInvoice.status}`, { defaultValue: ov.lastInvoice.status }), tone: statusTone[ov.lastInvoice.status] ?? 'neutral' } : undefined}
          trailing={ov?.lastInvoice ? fmt(ov.lastInvoice.total, ov.lastInvoice.currencyCode) : undefined}
          emptyText={t('dashboard.none', { defaultValue: '—' })} />
        <LatestCard icon={ClipboardList} title={t('dashboard.lastOrder', { defaultValue: 'Last order' })} loading={loading}
          primary={ov?.lastOrder?.docNumber} to={ov?.lastOrder ? `/sales/orders/${ov.lastOrder.id}` : undefined}
          secondary={ov?.lastOrder ? formatDate(ov.lastOrder.docDate, { locale: i18n.language }) : undefined}
          badge={ov?.lastOrder ? { text: ov.lastOrder.status, tone: statusTone[ov.lastOrder.status] ?? 'neutral' } : undefined}
          trailing={ov?.lastOrder ? fmt(ov.lastOrder.total, ov.lastOrder.currencyCode) : undefined}
          emptyText={t('dashboard.none', { defaultValue: '—' })} />
        <LatestCard icon={Package} title={t('dashboard.lastProduct', { defaultValue: 'Last product added' })} loading={loading}
          primary={ov?.lastProduct?.name} to={ov?.lastProduct ? `/inventory/items/${ov.lastProduct.id}` : undefined}
          secondary={ov?.lastProduct?.code}
          trailing={ov?.lastProduct ? formatDate(ov.lastProduct.createdAt, { locale: i18n.language }) : undefined}
          emptyText={t('dashboard.none', { defaultValue: '—' })} />
      </div>

      {/* Notifications · New products · Top sellers · Low stock · Top debtors · Recent activity */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {/* The bell's feed on the page itself — same rows, same words. Rows
            navigate but never mark-seen (the bell owns that). */}
        <ListCard icon={Bell} title={t('dashboard.notifications', { defaultValue: 'Notifications' })}
          loading={notifs === null} empty={notifs !== null && notifs.length === 0}
          emptyText={t('dashboard.noNotifications', { defaultValue: 'Nothing new — sales and orders will land here as they happen.' })}>
          {(notifs ?? []).map((n) => {
            const NIcon = EVENT_ICONS[n.eventType] ?? Bell;
            const row = (
              <>
                <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full ${n.unread ? 'bg-brand/15 text-brand' : 'bg-bg-subtle text-fg-muted'}`}>
                  <NIcon className="h-3.5 w-3.5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-sm ${n.unread ? 'font-semibold' : 'font-medium'}`}>{titleOf(t, n)}</span>
                  <span className="block truncate text-xs text-fg-muted">{bodyOf(t, i18n.language, n) || fmtDateTime(n.createdAt)}</span>
                </span>
              </>
            );
            return n.linkPath ? (
              <Link key={n.id} to={n.linkPath} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-bg-subtle">{row}</Link>
            ) : (
              <div key={n.id} className="flex items-center gap-3 rounded-md px-2 py-1.5">{row}</div>
            );
          })}
        </ListCard>
        {canReadItems && (
          <ListCard icon={PackagePlus} title={t('dashboard.newProducts', { defaultValue: 'New products' })} to="/inventory/items"
            loading={loading} empty={!(ov?.newestProducts?.length)} emptyText={t('dashboard.noProducts', { defaultValue: 'No products yet.' })}>
            {(ov?.newestProducts ?? []).map((p) => (
              <Link key={p.id} to={`/inventory/items/${p.id}`} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-bg-subtle">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{p.name}</span>
                  <span className="block truncate font-mono text-xs text-fg-muted">{p.code}</span>
                </span>
                <span className="shrink-0 text-xs text-fg-muted">{formatDate(p.createdAt, { locale: i18n.language })}</span>
              </Link>
            ))}
          </ListCard>
        )}
        {canReadItems && (
          <ListCard icon={TrendingUp} title={t('dashboard.topSellers', { defaultValue: 'Top sellers' })} to="/inventory/items"
            loading={loading} empty={!sellers.length} emptyText={t('dashboard.noSales', { defaultValue: 'No sales recorded yet.' })}>
            {sellers.map((s, i) => (
              <Link key={s.itemId} to={`/inventory/items/${s.itemId}`} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-bg-subtle">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand/10 text-xs font-bold text-brand">{i + 1}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{s.name}</span>
                  <span className="block truncate font-mono text-xs text-fg-muted">{s.code}</span>
                </span>
                <span className="shrink-0 font-mono text-sm font-semibold">{fmtCount(s.qtySold)}</span>
              </Link>
            ))}
          </ListCard>
        )}
        {canReadStock && (
          <ListCard icon={PackageX} title={t('dashboard.lowStock', { defaultValue: 'Low stock' })} to="/inventory/stock"
            loading={loading} empty={!lowStock.length} emptyText={t('dashboard.lowStockEmpty', { defaultValue: "Everything's well stocked." })}>
            {lowStock.map((it) => (
              <Link key={it.itemId} to={`/inventory/items/${it.itemId}`} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-bg-subtle">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{it.name}</span>
                  <span className="block truncate font-mono text-xs text-fg-muted">{it.code}</span>
                </span>
                <Badge tone={it.quantity <= 0 ? 'danger' : 'warning'}>
                  {it.quantity <= 0
                    ? t('dashboard.outOfStock', { defaultValue: 'Out' })
                    : t('dashboard.qtyLeft', { defaultValue: '{{n}} left', n: fmtCount(it.quantity) })}
                </Badge>
              </Link>
            ))}
          </ListCard>
        )}
        <ListCard icon={Wallet} title={t('dashboard.topDebtors', { defaultValue: 'Top debtors' })} to="/reports/ar-aging"
          loading={loading} empty={!ov?.topDebtors.length} emptyText={t('dashboard.noDebt', { defaultValue: 'No outstanding balances.' })}>
          {ov?.topDebtors.map((d) => (
            <div key={`${d.partyId}:${d.currencyCode}`} className="flex items-center gap-3 rounded-md px-2 py-1.5">
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{d.partyName}</span>
              <span className="shrink-0 font-mono text-sm font-semibold text-danger">{fmt(d.total, d.currencyCode)}</span>
            </div>
          ))}
        </ListCard>

        <ListCard icon={Activity} title={t('dashboard.recentActivity', { defaultValue: 'Recent activity' })} to="/settings/audit-log"
          loading={loading} empty={!ov?.lastActivity.length} emptyText={t('dashboard.noActivity', { defaultValue: 'No recent activity.' })}>
          {ov?.lastActivity.map((a) => (
            <div key={a.id} className="flex items-start gap-2 rounded-md px-2 py-1.5">
              <Badge tone={opTone[a.operation ?? ''] ?? 'neutral'}>{a.operation ?? a.action}</Badge>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{a.entityType.replace(/_/g, ' ')}</span>
                <span className="block truncate text-xs text-fg-muted">{a.actorName ?? '—'} · {fmtDateTime(a.at)}</span>
              </span>
            </div>
          ))}
        </ListCard>
      </div>

      {/* Recent invoices — only for users who can read invoices (else the empty
          state would falsely read "No invoices yet" on a 403). */}
      {canReadInvoices && (
      <Card className="overflow-x-auto p-0">
        <header className="flex items-center justify-between p-4">
          <h2 className="text-base font-semibold">{t('dashboard.recentInvoices', { defaultValue: 'Recent invoices' })}</h2>
          <Link to="/sales/invoices">
            <Button variant="ghost" size="sm">{t('actions.viewAll', { defaultValue: 'View all' })}</Button>
          </Link>
        </header>
        {!invoices ? (
          <div className="flex flex-col gap-3 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between gap-4">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-8 text-center">
            <Receipt className="h-8 w-8 text-fg-subtle" aria-hidden />
            <div>
              <h3 className="text-sm font-semibold">{t('dashboard.noInvoices.title', { defaultValue: 'No invoices yet' })}</h3>
              <p className="mt-1 max-w-sm text-sm text-fg-muted">
                {t('dashboard.noInvoices.description', { defaultValue: 'Issue your first invoice to a customer and it will show up right here.' })}
              </p>
            </div>
            {canCreateInvoice && (
              <Link to="/sales/invoices/new">
                <Button size="sm">{t('dashboard.newInvoice', { defaultValue: 'New invoice' })}</Button>
              </Link>
            )}
          </div>
        ) : (
          <table className="w-full text-start text-sm">
            <thead className="bg-bg-muted text-fg-subtle">
              <tr>
                <th className="px-4 py-2 text-start">#</th>
                <th className="px-4 py-2 text-start">{t('dashboard.date', { defaultValue: 'Date' })}</th>
                <th className="px-4 py-2 text-start">{t('dashboard.status', { defaultValue: 'Status' })}</th>
                <th className="px-4 py-2 text-end">{t('dashboard.total', { defaultValue: 'Total' })}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-border">
                  <td className="px-4 py-2 font-mono text-xs">
                    <Link to={`/sales/invoices/${inv.id}`} className="font-medium text-fg underline-offset-4 hover:underline hover:text-brand focus:outline-none focus-visible:underline">
                      {inv.docNumber}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-fg-muted">{formatDate(inv.docDate, { locale: i18n.language })}</td>
                  <td className="px-4 py-2">
                    <Badge tone={statusTone[inv.status] ?? 'neutral'}>
                      {tDoc(`invoices.status.${inv.status}`, { defaultValue: inv.status })}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-end font-mono">
                    {formatMoney(inv.total, { locale: i18n.language, currency: inv.currencyCode })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      )}

      {/* Quick actions — user-customizable shortcuts (incl. POS) */}
      <QuickActions />
    </div>
  );
}

function Kpi({
  label, amount, format, loading, linkTo, alert, icon, spine, hint, delta,
}: {
  label: string;
  amount: number | null;
  format: (n: number) => string;
  loading?: boolean;
  linkTo?: string;
  alert?: boolean;
  icon?: LucideIcon;
  spine?: string;
  hint?: string;
  delta?: { value: string; positive: boolean } | null;
}): JSX.Element {
  const isLoading = loading || amount === null;
  return (
    <StatCard
      label={label}
      value={amount === null ? '' : <AnimatedNumber value={amount} format={format} />}
      loading={isLoading}
      hint={hint}
      delta={delta}
      icon={icon}
      accent={alert ? 'var(--color-danger)' : (spine ?? 'var(--color-brand)')}
      to={linkTo}
    />
  );
}

function ChartCard({
  title, loading, empty, hide, emptyText, children,
}: {
  title: string;
  loading?: boolean;
  empty?: boolean;
  /** Render nothing at all (e.g. the caller lacks the permission for this chart's data). */
  hide?: boolean;
  emptyText: string;
  children: ReactNode;
}): JSX.Element | null {
  if (hide) return null;
  return (
    <Card className="flex flex-col p-4">
      <h2 className="mb-3 text-base font-semibold">{title}</h2>
      {loading ? (
        <Skeleton className="h-56 w-full" />
      ) : empty ? (
        <div className="grid h-56 place-items-center text-sm text-fg-muted">{emptyText}</div>
      ) : (
        // Recharts axes assume LTR; force it so RTL locales don't mirror the chart.
        <div className="h-56 w-full" dir="ltr">{children}</div>
      )}
    </Card>
  );
}

function ListCard({
  icon: Icon, title, to, loading, empty, emptyText, children,
}: {
  icon: LucideIcon;
  title: string;
  to?: string;
  loading?: boolean;
  empty?: boolean;
  emptyText: string;
  children?: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card className="flex flex-col p-0">
      <header className="flex items-center justify-between gap-2 p-4 pb-2">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Icon className="h-4 w-4 text-fg-muted" aria-hidden />{title}
        </h2>
        {to && <Link to={to}><Button variant="ghost" size="sm">{t('actions.viewAll', { defaultValue: 'View all' })}</Button></Link>}
      </header>
      <div className="flex flex-col gap-0.5 p-2 pt-0">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="mx-2 my-1 h-8" />)
        ) : empty ? (
          <p className="px-2 py-6 text-center text-sm text-fg-muted">{emptyText}</p>
        ) : (
          children
        )}
      </div>
    </Card>
  );
}

function LatestCard({
  icon: Icon, title, primary, secondary, badge, trailing, to, loading, emptyText,
}: {
  icon: LucideIcon;
  title: string;
  primary?: string;
  secondary?: string;
  badge?: { text: string; tone: 'success' | 'warning' | 'danger' | 'info' | 'neutral' };
  trailing?: string;
  to?: string;
  loading?: boolean;
  emptyText: string;
}): JSX.Element {
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-fg-muted">
          <Icon className="h-3.5 w-3.5" aria-hidden />{title}
        </span>
        {trailing && <span className="shrink-0 font-mono text-sm font-semibold">{trailing}</span>}
      </div>
      {loading ? (
        <Skeleton className="mt-2 h-5 w-32" />
      ) : primary ? (
        <>
          <div className="mt-1.5 truncate text-lg font-semibold">{primary}</div>
          <div className="mt-0.5 flex items-center gap-2">
            {secondary && <span className="truncate font-mono text-xs text-fg-muted">{secondary}</span>}
            {badge && <Badge tone={badge.tone}>{badge.text}</Badge>}
          </div>
        </>
      ) : (
        <div className="mt-1.5 text-lg font-semibold text-fg-subtle">{emptyText}</div>
      )}
    </>
  );
  return to && !loading && primary ? (
    <Link to={to}><Card className="h-full transition hover:border-brand/40">{body}</Card></Link>
  ) : (
    <Card className="h-full">{body}</Card>
  );
}
